import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import orderBookService, { OrderBookData, MarketDepthData } from '../services/orderBookService';
import serviceManager from '../services/serviceManager';

/**
 * React hook for using order book data
 * 
 * This hook provides:
 * 1. Real-time order book data
 * 2. Market depth data
 * 3. Connection status
 * 4. Error handling
 * 5. Automatic reconnection
 */
export const useOrderBookData = (symbol: string) => {
  const { user } = useAuth();
  
  // State
  const [orderBookData, setOrderBookData] = useState<OrderBookData | null>(null);
  const [marketDepthData, setMarketDepthData] = useState<MarketDepthData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  
  // Refs to prevent infinite loops
  const unsubscribeOrderBookRef = useRef<(() => void) | null>(null);
  const unsubscribeDepthRef = useRef<(() => void) | null>(null);
  const symbolRef = useRef(symbol);
  const userRef = useRef(user);
  const isSubscribedRef = useRef(false);
  const isMountedRef = useRef(true);
  
  // Update refs when values change
  useEffect(() => {
    symbolRef.current = symbol;
  }, [symbol]);
  
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  // Cleanup function
  const cleanup = useCallback(() => {
    if (unsubscribeOrderBookRef.current) {
      unsubscribeOrderBookRef.current();
      unsubscribeOrderBookRef.current = null;
    }
    if (unsubscribeDepthRef.current) {
      unsubscribeDepthRef.current();
      unsubscribeDepthRef.current = null;
    }
    isSubscribedRef.current = false;
    if (isMountedRef.current) {
      setIsConnected(false);
    }
  }, []);

  // Helper function to check if order book data has meaningful values
  const hasValidOrderBookData = useCallback((data: OrderBookData) => {
    const minRows = 1; // Minimum 6 rows required
    return data && 
           data.bids && data.bids.length >= minRows && 
           data.asks && data.asks.length >= minRows &&
           data.bids.filter(bid => bid.price > 0 && bid.quantity > 0).length >= minRows &&
           data.asks.filter(ask => ask.price > 0 && ask.quantity > 0).length >= minRows;
  }, []);

  // Helper function to check if market depth data has meaningful values
  const hasValidMarketDepthData = useCallback((data: MarketDepthData) => {
    const minRows = 1; // Minimum 6 rows required
    return data && 
           data.sells && data.sells.length >= minRows && 
           data.buys && data.buys.length >= minRows &&
           data.sells.filter(sell => sell.price > 0 && sell.quantity > 0).length >= minRows &&
           data.buys.filter(buy => buy.price > 0 && buy.quantity > 0).length >= minRows;
  }, []);

  // Stable callback functions that use refs to prevent infinite loops
  const handleOrderBookUpdate = useCallback((updatedSymbol: string, data: OrderBookData) => {
    if (updatedSymbol === symbolRef.current && isMountedRef.current) {
      setOrderBookData(data);
      setIsConnected(true);
      // Only set loading to false if we have valid data with real values
      if (hasValidOrderBookData(data)) {
        setIsLoading(false);
      }
    }
  }, [hasValidOrderBookData]);

  const handleMarketDepthUpdate = useCallback((updatedSymbol: string, data: MarketDepthData) => {
    if (updatedSymbol === symbolRef.current && isMountedRef.current) {
      setMarketDepthData(data);
      setIsConnected(true);
      // Only set loading to false if we have valid data with real values
      if (hasValidMarketDepthData(data)) {
        setIsLoading(false);
      }
    }
  }, [hasValidMarketDepthData]);

  // Subscribe function - stable reference
  const subscribe = useCallback(() => {
    if (!symbolRef.current || !userRef.current || !serviceManager.areServicesRunning()) {
      if (!userRef.current && isMountedRef.current) {
        setError('User must be authenticated to access order book data');
      }
      if (isMountedRef.current) {
        setIsLoading(false);
      }
      return;
    }

    if (isSubscribedRef.current) {
      return; // Already subscribed
    }

    try {
      if (isMountedRef.current) {
        setIsLoading(true);
        setError(null);
      }

      // Subscribe to order book updates
      const unsubscribeOrderBook = orderBookService.subscribeToOrderBook(
        symbolRef.current,
        handleOrderBookUpdate
      );

      // Subscribe to market depth updates
      const unsubscribeDepth = orderBookService.subscribeToMarketDepth(
        symbolRef.current,
        handleMarketDepthUpdate
      );

      unsubscribeOrderBookRef.current = unsubscribeOrderBook;
      unsubscribeDepthRef.current = unsubscribeDepth;
      isSubscribedRef.current = true;

    } catch (err) {
      console.error('Error subscribing to order book:', err);
      if (isMountedRef.current) {
        setError('Failed to connect to order book data');
        setIsLoading(false);
      }
    }
  }, [handleOrderBookUpdate, handleMarketDepthUpdate]); // Add dependencies for the handlers

  // Main effect - only runs when symbol or user changes
  useEffect(() => {
    isMountedRef.current = true;
    
    // Set loading state when symbol changes
    if (isMountedRef.current) {
      setIsLoading(true);
      setError(null);
    }
    
    // Cleanup previous subscription
    cleanup();
    
    // Subscribe to new symbol/user
    subscribe();

    // Cleanup on unmount
    return () => {
      isMountedRef.current = false;
      cleanup();
    };
  }, [symbol, user]);

  // Reconnect function
  const reconnect = useCallback(() => {
    cleanup();
    setTimeout(() => {
      if (isMountedRef.current) {
        subscribe();
      }
    }, 1000);
  }, [cleanup, subscribe]);

  // Get current order book data
  const getOrderBookData = useCallback(() => {
    return orderBookData;
  }, [orderBookData]);

  // Get current market depth data
  const getMarketDepthData = useCallback(() => {
    return marketDepthData;
  }, [marketDepthData]);

  // Check if connected
  const checkConnection = useCallback(() => {
    return isConnected;
  }, [isConnected]);

  return {
    // Order book data properties
    bids: orderBookData?.bids || [],
    asks: orderBookData?.asks || [],
    spread: orderBookData?.spread || 0,
    spreadPercentage: orderBookData?.spreadPercentage || 0,
    
    // Market depth data properties
    sells: marketDepthData?.sells || [],
    buys: marketDepthData?.buys || [],
    maxVolume: marketDepthData?.maxVolume || 0,
    
    // Status
    isLoading,
    error,
    isConnected,
    
    // Actions
    reconnect,
    refreshData: reconnect, // Alias for backward compatibility
    getOrderBookData,
    getMarketDepthData,
    checkConnection,
  };
};

export default useOrderBookData;